/**
 * @module experience/change-plan-services
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
// core libraries
import { Injectable } from '@angular/core';
import { throwError as observableThrowError, Subject, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
// service
import { OrderSummaryService } from '@experience/order-summary/service/order-summary.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
// Interface
import { Api416Response } from '@support/api/proxies/responses/api-416.interface';
import { CancelOrderData } from '@support/api/proxies/requests/api-2815.interface';
import { CancelOrderDetails } from '@support/api/proxies/responses/api-2815.interface';
import { APIError } from '@support/api/common/error.interface';
import { OrderSearchResults } from '@support/api/proxies/responses/api-2813.interface';
import { OrderSummaryInterface } from '@support/api/proxies/responses/api-2814-new.interface';
// Models
import { PendingOrderModel } from '@experience/change-plan-services/model/pending-order.model';
// constants
import {
  ORDER_SUMMARY_CONSTANT,
  ORDER_TYPE_CONSTANT,
} from '@experience/order-summary/order-summary.constants';
import { CPS_CONSTANTS } from '@experience/change-plan-services/constants/change-plan-services.constants';
import { Wove } from 'aspect.js';
import { HttpResponse, HttpHeaders } from '@angular/common/http';

@Wove({ moduleName: ORDER_TYPE_CONSTANT.CHANGE_PLAN_SERVICE_MODULE })
@Injectable()
export class PendingOrdersService {
  /**
   * Subject Object to hold Obserbable data of PendingOrderModel type
   * @private
   * @type {Subject<PendingOrderModel[]>}
   * @memberof PendingOrdersService
   */
  private pendingOrderModelSubject$: Subject<PendingOrderModel[]>;
  /**
   * Obserbable Object to emit PendingOrderModel type
   */
  public pendingOrderModelSubjectObservable: Observable<PendingOrderModel[]>;

  constructor(
    private orderSummaryService: OrderSummaryService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private apiGatewayService: ApiGatewayService
  ) {
    this.pendingOrderModelSubject$ = new Subject<PendingOrderModel[]>();
    this.pendingOrderModelSubjectObservable = this.pendingOrderModelSubject$.asObservable();
  }
  /** Public method to lock or Unlock the order while canceling i t
   *  @param {string} orderId
   *  @param {string} actionCode
   *  @returns Observable<Api416Response>
   */
  public lockUnlockOrder(orderId?: string, actionCode?: string): Observable<Api416Response> {
    return this.orderSummaryService.lockUnlockOrder(true, actionCode, true, orderId);
  }

  /**
   * Gets the pending orders data from API_2813 and API_2814
   * @returns void
   * @memberof PendingOrdersService
   */
  public getPendingOrderDetails(): void {
    const lookUpParams: { [name: string]: string } = {};
    let pendingOrderModel: PendingOrderModel;
    let pendingOrderCount: number = 0;
    const pendingOrder: PendingOrderModel[] = [];
    // @TODO: to update this on the basis of selected msisdn and will be handled in US296709
    lookUpParams.msisdn = this.stateStoreUtilsService.getLineMsisdns()[0];
    this.orderSummaryService
      .getPendingOrderDetails(lookUpParams)
      .subscribe((orderSearchResults: OrderSearchResults[]) => {
        const orderSearchResultsFilterList = orderSearchResults.filter(
          (element: OrderSearchResults) => {
            return (
              element.orderStatus === CPS_CONSTANTS.ORDER_STATUS_INPROCESS ||
              element.orderStatus === CPS_CONSTANTS.ORDER_STATUS_RECEIVED
            );
          },
          (error: APIError) => {
            observableThrowError(error);
          }
        );
        const orderSearchResultsLength: number = orderSearchResultsFilterList.length;
        orderSearchResultsFilterList.forEach((orderSearchData: OrderSearchResults) => {
          this.orderSummaryService.getOrderSummary(null, false, orderSearchData.orderId).subscribe(
            (orderInformationInterface: OrderSummaryInterface) => {
              pendingOrderCount++;
              pendingOrderModel = new PendingOrderModel(orderInformationInterface);
              pendingOrderModel.pendingOrdersDataDetails.orderType = orderSearchData.orderType;
              pendingOrder.push(pendingOrderModel);
              if (orderSearchResultsLength === pendingOrderCount) {
                this.pendingOrderModelSubject$.next(pendingOrder);
              }
            },
            (error: APIError) => {
              observableThrowError(error);
            }
          );
        });
      });
  }
  /**
   * Method to cancel pending order for the given order id
   * @param {string} orderId
   * @returns {boolean}
   */
  public cancelPendingOrder(orderId: string, orderStatus: string): Observable<CancelOrderDetails> {
    const headers = new HttpHeaders();
    const payload: CancelOrderData = {
      orderId: orderId,
      orderStatus: orderStatus,
      // TO-DO As per discussion with PO, need to pass it as hard coded value.
      reasonCode: ORDER_SUMMARY_CONSTANT.REASONCODE,
    };
    const pathParams = {
      orderId: orderId,
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2815_postCancelOrder, payload, headers, null, pathParams)
      .pipe(
        map((response: HttpResponse<CancelOrderDetails>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }
}
